///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, OnDestroy, OnInit } from '@angular/core';
import { GtthHistoryService } from '../../../logical-topology/engine/history-service';
import { Subscription } from 'rxjs';
import { GtthReportsService } from '../../../../services/project/reports.service';
import { GtthReportsTabs, REPORT_TABS } from '../reports-tabs.enum';
import { AbstractInteractiveWindow } from '../../abstract-interactive-window';
import { ProjectInteractiveWindowsPopupService } from '../../project-interactive-windows-popup.service';
import { _cloneDeep } from '../../../../shared/utils';
import { PARAM_BBOX } from '../../../../shared/constants/url-params.constants';
import { ActivatedRoute } from '@angular/router';
import { GtthProjectInfoViewService } from '../../../../layouts/navbar/project-info-view.service';
import { GtthNodeWithInterferenceDTO } from '../../../../generated/shared-types.model';
import { MatDialogRef } from '@angular/material/dialog';
import { PreloaderService } from '../../../../services/preloader.service';
import { isDefined } from '@siklu/sui-lib/utils';

@Component({
    selector: 'winde-report-nodes-with-interference',
    templateUrl: './nodes-with-interference.component.html',
    styleUrls: ['../../project-windows.scss', './nodes-with-interference.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class GtthReportNodesWithInterferenceComponent extends AbstractInteractiveWindow implements OnInit, OnDestroy {
    nodesList;
    nodesNumber: number;
    collapseNodeList: boolean;
    showNode: string;
    nodesListToShow;
    public currentTab = GtthReportsTabs.ALL;
    public reportsTabs = GtthReportsTabs;
    public readonly tabsToShow = REPORT_TABS;
    public layers: Record<string, string>;
    private layersChangedSubscriber: Subscription;

    constructor(
        public dialogRef: MatDialogRef<GtthReportNodesWithInterferenceComponent>,
        private readonly elementRef: ElementRef,
        private readonly changeDetector: ChangeDetectorRef,
        private readonly historyService: GtthHistoryService,
        private readonly reportsService: GtthReportsService,
        private readonly preloaderService: PreloaderService,
        private readonly projectInfoService: GtthProjectInfoViewService
    ) {
        super(elementRef);

        this.layersChangedSubscriber = projectInfoService.layerChangedSubscription().subscribe((value) => {
            if (this.nodesList) {
                this.filterListToShow(value);
            }
            this.getProjectLayers();
            this.changeDetector.markForCheck();
        });
    }

    ngOnInit() {
        this.preloaderService.show();

        this.collapseNodeList = true;

        this.getProjectLayers();
        this.reportsService.getLinksEndsWithInterference().subscribe((res: Record<string, GtthNodeWithInterferenceDTO[]>) => {
            this.nodesList = this.filterNodesListByFrozen(res);
            this.filterListToShow(this.projectInfoService.activeLayer);
            this.nodesNumber = this.nodesListToShow ? this.nodesListToShow.length : 0;
            this.preloaderService.hide();
            for (const link of this.historyService.logicalTopology.getSelectedLinks()) {
                const fromNode = link.getFromStation().getName();
                const toNode = link.getToStation().getName();
                this.linkSelected(fromNode, toNode, true);
            }
            this.changeDetector.markForCheck();
        });
    }

    ngOnDestroy() {
        if (this.layersChangedSubscriber) {
            this.layersChangedSubscriber.unsubscribe();
            this.layersChangedSubscriber = null;
        }
    }

    getProjectLayers() {
        if (this.projectInfoService.layersOptions?.length) {
            this.layers = this.projectInfoService.getLayersAsKeyValueObj();
        }
    }

    linkSelected(fromName: string, toName: string, selected: boolean) {
        this.nodesListToShow.forEach((node) => {
            if (node.nodeName === fromName) {
                const linkIndex = node.secondNodesList.findIndex((link) => {
                    return link.nodeName === toName;
                });
                if (linkIndex !== -1) {
                    node.secondNodesList[linkIndex].selected = selected;
                    if (selected) {
                        this.showNode = node.nodeName;
                    }
                }
            }
        });

        this.changeDetector.markForCheck();
    }

    linkFrozen(fromName: string, toName: string, isForced: boolean) {
        this.changeFrozenInstalled(fromName, toName, isForced);
    }

    linkInstalled(fromName: string, toName: string, isInstalled: boolean) {
        this.changeFrozenInstalled(fromName, toName, undefined, isInstalled);
    }

    closeModal() {
        this.nodesList = undefined;
        this.nodesListToShow = undefined;
        this.dialogRef.close('cancel');
        this.changeDetector.markForCheck();
    }

    trackNodeName(_index, item) {
        return item.nodeName;
    }

    selectLink(nodeFrom: string, nodeTo: string) {
        this.historyService.clearSelectionCloseWindowAndArea();
        const link = this.historyService.logicalTopology.getLinkByNodeNames(nodeFrom, nodeTo);
        if (link) {
            this.historyService.logicalTopology.addSelectedLink(link);
        }
    }

    changeTab(tabIndex: string) {
        this.currentTab = GtthReportsTabs[tabIndex];
        this.filterListToShow(this.projectInfoService.activeLayer);
    }

    @HostListener('document:keydown', ['$event'])
    onKeydown(event: KeyboardEvent) {
        if (event.key === 'Escape' && !this.historyService.hasSearchMarkers()) {
            this.closeModal();
        }
    }

    designRequired(): boolean {
        return true;
    }

    isRestorable(): boolean {
        return true;
    }

    private changeFrozenInstalled(fromName: string, toName: string, isForced: boolean, isInstalled?: boolean) {
        this.nodesListToShow.forEach((node) => {
            if (node.nodeName !== fromName && node.nodeName !== toName) {
                return;
            }
            node.secondNodesList.forEach((secondNode) => {
                if (secondNode.nodeName !== fromName && secondNode.nodeName !== toName) {
                    return;
                }
                secondNode.isUserForced = isDefined(isForced) ? isForced : secondNode.isUserForced;
                secondNode.isInstalled = isDefined(isInstalled) ? isInstalled : secondNode.isInstalled;
            });
        });
    }

    private filterNodesListByFrozen(nodesList: Record<string, GtthNodeWithInterferenceDTO[]>) {
        if (!nodesList || Object.keys(nodesList).length === 0) {
            return [];
        } else {
            const createdNodesList = [];
            Object.keys(nodesList).forEach((nodeName) => {
                const newNodesList = [];
                nodesList[nodeName].forEach((secondNodeData) => {
                    const link = this.historyService.logicalTopology.getLinkByNodeNames(nodeName, secondNodeData.secondNodeName);
                    newNodesList.push({
                        nodeName: secondNodeData.secondNodeName,
                        ciRatio: secondNodeData.ctoidb,
                        isUserForced: link.isUserForced(),
                        isInstalled: link.isInstalled(),
                        layerIndex: secondNodeData.layerIndex
                    });
                });
                createdNodesList.push({
                    nodeName,
                    secondNodesList: newNodesList
                });
            });
            return createdNodesList;
        }
    }

    private filterListToShow(layerIndex = -1) {
        this.nodesListToShow = [];

        switch (this.currentTab) {
            case GtthReportsTabs.ALL:
                if (layerIndex === -1) {
                    this.nodesListToShow = _cloneDeep(this.nodesList);
                } else {
                    this.nodesList.forEach((node) => {
                        const newSecondNodeList = node.secondNodesList.filter((secondNode) => {
                            return secondNode.layerIndex === layerIndex;
                        });
                        this.createNodeListToShow(node, newSecondNodeList);
                    });
                }
                break;

            case GtthReportsTabs.NEW:
                this.nodesList.forEach((node) => {
                    const newSecondNodeList = node.secondNodesList.filter((secondNode) => {
                        return !secondNode.isUserForced && (layerIndex === -1 || secondNode.layerIndex === layerIndex);
                    });
                    this.createNodeListToShow(node, newSecondNodeList);
                });
                break;

            case GtthReportsTabs.INSTALLED:
                this.nodesList.forEach((node) => {
                    const installedSecondNodeList = node.secondNodesList.filter((secondNode) => {
                        return secondNode.isInstalled && (layerIndex === -1 || secondNode.layerIndex === layerIndex);
                    });
                    this.createNodeListToShow(node, installedSecondNodeList);
                });
                break;
        }
    }

    private createNodeListToShow(node, secondNodeList) {
        if (secondNodeList && secondNodeList.length > 0) {
            const newNode = { ...node };
            newNode.secondNodesList = secondNodeList;
            this.nodesListToShow.push(newNode);
        }
    }
}

@Component({
    selector: 'winde-report-nodes-with-interference-popup',
    template: '',
    standalone: false
})
export class GtthReportNodesWithInterferencePopupComponent implements OnInit {
    constructor(
        private readonly interactiveWindowsPopupService: ProjectInteractiveWindowsPopupService,
        private readonly activatedRoute: ActivatedRoute
    ) {}

    ngOnInit() {
        let bboxParams;
        if (this.activatedRoute.snapshot.queryParamMap.has(PARAM_BBOX)) {
            bboxParams = this.activatedRoute.snapshot.queryParamMap
                .get(PARAM_BBOX)
                .split(',')
                .map((value) => parseFloat(value));
        }
        const routerPath = this.activatedRoute.routeConfig.path;
        this.interactiveWindowsPopupService.openInteractiveWindow(
            GtthReportNodesWithInterferenceComponent,
            ProjectInteractiveWindowsPopupService.REP_NODES_WITH_INTERFERENCE,
            routerPath,
            bboxParams
        );
    }
}
